import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import pt from './pt.json';
import es from './es.json';
import type { ReportLocale } from './resolveLocale';

type TranslationVars = Record<string, string | number>;

type TranslateFn = (key: string, fallback?: string, vars?: TranslationVars) => string;

interface ReportI18nContextValue {
  locale: ReportLocale;
  showLatamSeal: boolean;
  t: TranslateFn;
}

const DICTIONARIES: Record<ReportLocale, Record<string, unknown>> = {
  pt,
  es,
};

const ReportI18nContext = createContext<ReportI18nContextValue | null>(null);

function readPath(source: unknown, key: string): unknown {
  return key.split('.').reduce<unknown>((current, segment) => {
    if (current && typeof current === 'object' && segment in current) {
      return (current as Record<string, unknown>)[segment];
    }
    return undefined;
  }, source);
}

function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = vars[name];
    return value === undefined ? `{${name}}` : String(value);
  });
}

function translate(locale: ReportLocale, key: string, fallback?: string, vars?: TranslationVars): string {
  const fromLocale = readPath(DICTIONARIES[locale], key);
  const fromPt = readPath(DICTIONARIES.pt, key);
  const raw = typeof fromLocale === 'string' ? fromLocale : typeof fromPt === 'string' ? fromPt : fallback ?? key;
  return interpolate(raw, vars);
}

interface ReportI18nProviderProps {
  locale: ReportLocale;
  children: ReactNode;
}

export function ReportI18nProvider({ locale, children }: ReportI18nProviderProps) {
  const t = useCallback<TranslateFn>(
    (key, fallback, vars) => translate(locale, key, fallback, vars),
    [locale]
  );

  const value = useMemo<ReportI18nContextValue>(
    () => ({
      locale,
      showLatamSeal: locale === 'es',
      t,
    }),
    [locale, t]
  );

  return <ReportI18nContext.Provider value={value}>{children}</ReportI18nContext.Provider>;
}

export function useReportI18n(): ReportI18nContextValue {
  const context = useContext(ReportI18nContext);
  if (context) {
    return context;
  }

  return {
    locale: 'pt',
    showLatamSeal: false,
    t: (key, fallback, vars) => translate('pt', key, fallback, vars),
  };
}
